import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useMaintenanceStore } from '../../store/maintenanceStore'
import api from '../../lib/api'

/**
 * Full-width banner that appears whenever the backend is in deployment
 * maintenance mode (nginx returning HTTP 503 with `{error:"maintenance"}`).
 *
 * While visible, the component polls a lightweight unauthenticated endpoint
 * every few seconds.  The first successful response triggers the response
 * interceptor in `lib/api.ts` to clear the maintenance flag → banner
 * unmounts automatically.  No manual dismissal — users should not be able to
 * hide the warning while the API is genuinely unavailable.
 */
export default function MaintenanceBanner() {
  const isActive = useMaintenanceStore((s) => s.isActive)
  const { t } = useTranslation('common')

  useEffect(() => {
    if (!isActive) return
    // Poll the unauthenticated feature-flags endpoint while in maintenance.
    // Short interval so the banner disappears within a couple of seconds of
    // the deploy finishing, but not so short that we hammer nginx during a
    // long rollback.
    const intervalId = window.setInterval(() => {
      // We don't care about the response — the interceptor handles state.
      // .catch keeps the unhandled-promise-rejection logs quiet while 503ing.
      api.get('/config/feature-flags').catch(() => undefined)
    }, 5000)
    return () => window.clearInterval(intervalId)
  }, [isActive])

  if (!isActive) return null

  return (
    <div
      role="alert"
      aria-live="polite"
      className="w-full bg-amber-500 text-amber-950 shadow-md border-b border-amber-600"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-3 text-sm font-medium">
        <svg
          className="w-5 h-5 flex-shrink-0 animate-pulse"
          fill="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
            clipRule="evenodd"
          />
        </svg>
        <span>{t('maintenance.message')}</span>
      </div>
    </div>
  )
}
